import emoji from "react-easy-emoji";
import splashAnimation from "./assets/lottie/wolfloading";

/* ============================================================
   PORTFOLIO CONFIG
   ------------------------------------------------------------
   This file is the source of truth for all site content.
   Edit the values below directly to update the portfolio.

   Images live in  public/assets/images/  and are referenced by
   their public path, e.g. "/assets/images/wolflogo.png".
   Drop a new file in that folder and point at it the same way.
   ============================================================ */

const theme = {
  accent: "#9a00ff",
  accent2: "#39ff14",
  bg: "#000000",
  panel: "#000000",
  text: "#eafff5",
  muted: "#00fff7",
  border: "#000000",
  tokens: {},
  sectionTokens: {},
  sections: {},
};

const seo = {
  title: "Georgios Grigoriadis | Gameplay Programmer",
  description: "Gameplay programmer specializing in Unity, multiplayer systems, and real-time interactions.",
  siteUrl: "https://ggrigoriadis.net/",
  ogImage: "",
  themeColor: "#00b74f",
  twitterHandle: "",
};

const branding = {};

const footer = {};

/* ============================================================
   TAGS
   ============================================================ */

const tagCatalog = [
  {
    name: "Analytics",
    slug: "analytics",
    color: "#38bdf8",
  },
  {
    name: "Canceled",
    slug: "canceled",
    color: "#ef4444",
  },
  {
    name: "C#",
    slug: "csharp",
    color: "#a855f7",
  },
  {
    name: "Editor Tool",
    slug: "editor-tool",
    color: "#a78bfa",
  },
  {
    name: "Elementor",
    slug: "elementor",
    color: "#d946ef",
  },
  {
    name: "GameJamPlus 24/25",
    slug: "gamejamplus-2425",
    color: "",
  },
  {
    name: "GDevelop",
    slug: "gdevelop",
    color: "#f59e0b",
  },
  {
    name: "Global Game Jam 2023",
    slug: "global-game-jam-2023",
    color: "",
  },
  {
    name: "Global Game Jam 2024",
    slug: "global-game-jam-2024",
    color: "",
  },
  {
    name: "Global Game Jam 2026",
    slug: "global-game-jam-2026",
    color: "",
  },
  {
    name: "In-Development",
    slug: "in-development",
    color: "#eab308",
  },
  {
    name: "Mini Jame Gam #43",
    slug: "mini-jame-gam-43",
    color: "",
  },
  {
    name: "Multiplayer",
    slug: "multiplayer",
    color: "#34d399",
  },
  {
    name: "Photon",
    slug: "photon",
    color: "#22d3ee",
  },
  {
    name: "Prototype",
    slug: "prototype",
    color: "#f97316",
  },
  {
    name: "Purple Cow Studios",
    slug: "purple-cow-studios",
    color: "",
  },
  {
    name: "React JS",
    slug: "react-js",
    color: "#22d3ee",
  },
  {
    name: "React",
    slug: "react",
    color: "#38bdf8",
  },
  {
    name: "Released",
    slug: "released",
    color: "#10b981",
  },
  {
    name: "Unity",
    slug: "unity",
    color: "#7c3aed",
  },
  {
    name: "Unreal Engine",
    slug: "unreal-engine",
    color: "#0ea5e9",
  },
  {
    name: "VR",
    slug: "vr",
    color: "#fb7185",
  },
  {
    name: "WordPress",
    slug: "wordpress",
    color: "#1d4ed8",
  },
];

// Derived from tagCatalog above — add colors there, not here.
const tagColorByName = tagCatalog.reduce((acc, tag) => {
  if (tag && tag.name) acc[tag.name] = tag.color || "";
  return acc;
}, {});

/* ============================================================
   SPLASH / ILLUSTRATION
   ============================================================ */

const splashScreen = {
  enabled: true,
  animation: splashAnimation,
  duration: 1500,
};

const illustration = {
  animated: false,
};

/* ============================================================
   GREETING
   ============================================================ */

const greeting = {
  username: "ggrigoriadis",
  title: "Hi! I'm George",
  subTitle: emoji("I'm 22 based in Athens, Greece focusing on multiplayer Systems and Gameplay mechanics"),
  logoIcon: null,
  taglines: [
    "Gameplay Programmer • Multiplayer & Networking",
    "Unity / C# • Unreal Engine / BP",
    "Tooling & Analytics for Designers",
  ],
  highlights: [],
  displayGreeting: true,
  logoImage: "/assets/images/wolflogo.png",
};

const socialMediaLinks = {
  github: "https://github.com/giorgosgrigoriadis",
  linkedin: "https://www.linkedin.com/in/georgios-grigoriadis-26064a200/",
  gmail: "giorgosgrigoriadis9@gmail.com",
  itchio: "https://ntiregoylfios.itch.io/",
  display: true,
};

/* ============================================================
   SKILLS
   ============================================================ */

const skillsSection = {
  title: "",
  subTitle: "",
  media: {
    src: "/assets/images/WebsiteTrailer.mp4",
  },
  skills: [
    emoji("⚡ I'm always looking for opportunities to learn and grow as a game developer."),
    emoji("⚡ Passionate about designing seamless multiplayer interactions and solving networking challenges."),
  ],
  softwareSkills: [
    {
      skillName: "Unity",
      fontAwesomeClassname: "fas fa-cube",
    },
    {
      skillName: "Unreal Engine",
      fontAwesomeClassname: "fas fa-gamepad",
    },
    {
      skillName: "C#",
      fontAwesomeClassname: "fas fa-code",
    },
    {
      skillName: "Photon",
      fontAwesomeClassname: "fas fa-bolt",
    },
    {
      skillName: "Mirror",
      fontAwesomeClassname: "fas fa-network-wired",
    },
    {
      skillName: "Git",
      fontAwesomeClassname: "fab fa-git-alt",
    },
    {
      skillName: "GitHub",
      fontAwesomeClassname: "fab fa-github",
    },
    {
      skillName: "Reactjs",
      fontAwesomeClassname: "fab fa-react",
    },
  ],
  display: true,
};

/* ============================================================
   EDUCATION
   ============================================================ */

const educationInfo = {
  display: true,
  schools: [
    {
      schoolName: "SAE Institute Athens",
      logo: "/assets/images/saeLogo.png",
      subHeader: "BSc in Games Programming",
      duration: "October 2022 – October 2025",
      desc: "Focused on game development with courses in gameplay programming, engine architecture, and multiplayer networking.",
      descBullets: [],
    },
  ],
};

/* ============================================================
   TECH STACK (progress bars)
   ============================================================ */

const techStack = {
  viewSkillBars: false,
  experience: [
    {
      Stack: "Unity",
      progressPercentage: "0%",
    },
    {
      Stack: "Backend",
      progressPercentage: "70%",
    },
    {
      Stack: "Programming",
      progressPercentage: "60%",
    },
  ],
  displayCodersrank: true,
};

/* ============================================================
   WORK EXPERIENCE
   ============================================================ */

const workExperiences = {
  display: true,
  experience: [
    {
      role: "Vice President",
      company: "GDA Greece",
      companylogo: "/assets/images/gdalogo.png",
      date: "Jan 2026 – Present",
      bg: "linear-gradient(180deg, #22D3EE 0%, #0B7285 55%, #062A33 100%)",
      desc: "",
      descBullets: [
        "Co-ordinator of the Union's Projects and part of the Presidium",
        "Contributing to the strategic planning and development of initiatives that promote Greek game creators and studios",
      ],
    },
    {
      role: "Lecturer",
      company: "IEK Intergraphics",
      companylogo: "/assets/images/iekintergraphicslogo.png",
      date: "Oct 2024 – Present",
      bg: "linear-gradient(180deg, #9AA7D8 0%, #5567A6 55%, #1E2A4A 100%)",
      desc: "",
      descBullets: [
        "Giving lectures on Unity/Unreal game engines and game development topics",
        "Mentoring students in troubleshooting their game development projects",
      ],
    },
    {
      role: "Customer Support & QA Lead",
      company: "Admate Hellas",
      companylogo: "/assets/images/admatelogo2.png",
      date: "Feb 2024 – Dec 2024",
      bg: "linear-gradient(180deg, #FF3B3B 0%, #B00000 55%, #2B0000 100%)",
      desc: "",
      descBullets: [
        "Managed 200+ clients across Greece",
        "Trained and mentored a junior team member from no experience to independently handling their assigned position",
      ],
    },
    {
      role: "Instructor",
      company: "Freelance",
      companylogo: "/assets/images/wolflogobg.png",
      date: "Jun 2022 – Aug 2023",
      bg: "linear-gradient(180deg, #66FF9A 0%, #15B86A 55%, #063A25 100%)",
      desc: "",
      descBullets: [
        "Delivered private C# and Unity lessons to 10+ freshmen, providing a strong foundation in the fundamentals of game development",
        "Taught Fortran programming to university students, helping them understand the fundamentals and practical applications",
      ],
    },
    {
      role: "Network System Consultant",
      company: "Purple Cow Studios",
      companylogo: "/assets/images/purplecowlogo.png",
      date: "Jun 2019 – Aug 2019",
      bg: "linear-gradient(180deg, #8E24AA 0%, #4A0F5E 55%, #160012 100%)",
      desc: "",
      descBullets: [
        "Reviewed and advised on multiplayer networking approaches, enhancing stability and performance",
        "Collaborated with developers to debug and enhance network performance and scalability",
      ],
    },
  ],
};

/* ============================================================
   OPEN SOURCE
   ============================================================ */

const openSource = {
  showGithubProfile: "true",
  display: true,
};

/* ============================================================
   PROJECTS (CATEGORY LISTS)
   ============================================================ */

const bigProjects = {
  title: "Games",
  subtitle: "",
  projects: [
    {
      id: "hover-lasers",
      image: "/assets/images/HoverLasersLogo.png",
      gif: "/assets/images/HoverLasersGif.gif",
      projectName: "Hover Lasers",
      projectDesc: "High-speed, high-stakes multiplayer racing game for up to 8 players.",
      longDescription: "Hover Lasers is a high-speed, high-stakes multiplayer racing game for up to 8 players. Dodge obstacles and fight for survival — if you fall, you're starting over. Best played with friends.",
      interactable: false,
      footerLink: [
        {
          name: "Itch.io",
          url: "https://ntiregoylfios.itch.io/hover-lasers",
        },
      ],
      techTags: ["Unity", "Photon", "In-Development"],
      year: 2025,
      platform: "PC",
      role: "Gameplay & Network",
      hoverGifEnabled: false,
      detailsPageEnabled: false,
      engine: "Unity",
      screenshots: ["https://img.itch.zone/aW1nLzIwNjk4MjYwLnBuZw==/original/oFUrqs.png"],
      controls: ["WASD — Move", "Spacebar — Ascend", "Shift — Boost", "R — Reset Bike"],
      howToPlay: [
        "Race across tracks packed with lasers and moving platforms.",
        "The longer you stay in high-speed mode, the more points you earn.",
        "Wipe out and you'll lose some of those hard-earned points.",
        "No checkpoints, no mercy — just skill.",
        "The game ends after 5 rounds or if no one makes it to the finish.",
        "Highest score wins. Speed is everything.",
      ],
      credits: [
        {
          name: "Georgios Grigoriadis",
          role: "Programming",
        },
      ],
    },
    {
      id: "rum-ram",
      image: "/assets/images/rumramlogo.png",
      projectName: "Rum Ram",
      projectDesc: "A fast-paced multiplayer brawler for up to 4 players focused on chaotic, physics-driven combat.",
      longDescription: "This game is all about pure, fast-paced mayhem — destroy everyone in the name of Rum! A sweet top-down multiplayer brawler where ramming, cannonballs, and mines settle every argument.",
      interactable: false,
      footerLink: [
        {
          name: "Steam",
          url: "https://store.steampowered.com/app/819940/Rum_Ram/",
        },
      ],
      techTags: ["Unreal Engine", "Released", "Purple Cow Studios"],
      year: 2019,
      platform: "PC",
      role: "Network",
      hoverGifEnabled: false,
      detailsPageEnabled: false,
      engine: "Unreal Engine",
      screenshots: [
        "https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/819940/ss_cb26a089c552a257ace7ef654349a68a4f946677.1920x1080.jpg",
      ],
      features: [
        "Ramming — the signature move that started it all",
        "Cannon balls for old-school ship-to-ship combat",
        "Mines to leave a nasty surprise for anyone on your tail",
        "In-game mocking emotes to taunt your friends",
      ],
      credits: [
        {
          name: "Pavlos Alifragis / Purple Cow",
          role: "Developer",
        },
        {
          name: "Georgios Grigoriadis",
          role: "Network Consultant",
        },
      ],
    },
    {
      id: "eternal-dungeons",
      image: "/assets/images/EternalDungeonlogo.png",
      projectName: "Eternal Dungeons",
      projectDesc: "3v3 dungeon race game with character selection and multiplayer competition.",
      longDescription: "Eternal Dungeons is a 3v3 dungeon race where two teams dive into parallel dungeons and compete to be the first to clear them. Pick a side, Red or Blue, choose one of three characters, and prepare to outrun your enemies. The project is a prototype — the future of it is unknown.",
      interactable: false,
      footerLink: [
        {
          name: "Itch.io",
          url: "https://ntiregoylfios.itch.io/eternal-dungeon",
        },
      ],
      techTags: ["Unity", "Photon", "Prototype"],
      year: 2024,
      platform: "PC",
      role: "Gameplay",
      hoverGifEnabled: false,
      detailsPageEnabled: false,
      engine: "Unity",
      screenshots: ["https://img.itch.zone/aW1nLzIwNzI3NzExLnBuZw==/original/sOKmJo.png"],
      controls: ["WASD — Move", "Mouse — Aim & Attack"],
      howToPlay: [
        "Team up in squads of 3.",
        "Pick your character.",
        "Enter your team's dungeon instance.",
        "The first team to clear the dungeon wins the game.",
      ],
      credits: [
        {
          name: "Georgios Grigoriadis",
          role: "Programming",
        },
        {
          name: "Danai Alissa",
          role: "Logo",
        },
      ],
    },
    {
      id: "eternal-dungeons-vr",
      image: "/assets/images/EternalDungeonsVRlogo.png",
      projectName: "Eternal Dungeons VR",
      projectDesc: "Single-player VR arena shooter with bow-based combat against waves of undead.",
      longDescription: "Eternal Dungeons VR is a single-player VR arena shooter where you face endless waves of undead with nothing but your bow and your instincts.",
      interactable: false,
      footerLink: [
        {
          name: "Itch.io",
          url: "https://ntiregoylfios.itch.io/eternal-dungeons-vr",
        },
      ],
      techTags: ["Unity", "Canceled"],
      year: 2024,
      platform: "PC",
      role: "Gameplay",
      hoverGifEnabled: false,
      detailsPageEnabled: false,
      engine: "Unity",
      screenshots: [
        "https://img.itch.zone/aW1hZ2UvMzQ3NDM4NS8yMDcyOTMzNS5wbmc=/original/Kf32JA.png",
        "https://img.itch.zone/aW1hZ2UvMzQ3NDM4NS8yMDcyOTMzNi5wbmc=/original/QSpWr5.png",
        "https://img.itch.zone/aW1hZ2UvMzQ3NDM4NS8yMDcyOTMzOS5wbmc=/original/i3WODp.png",
        "https://img.itch.zone/aW1hZ2UvMzQ3NDM4NS8yMDcyOTM0MS5wbmc=/original/NAfrnp.png",
      ],
      controls: [
        "VR motion controls",
        "Draw the bow fully for a powerful shot",
        "Arrows change visually when charged",
        "Requires a VR headset + controllers",
      ],
      howToPlay: [
        "Go to the teleporter in front of the giant skull.",
        "Use your bow to shoot the undead down.",
        "Fully drawing the bow powers up your shot and changes the arrow.",
        "Hit them hard enough and you'll knock bones clean off.",
      ],
      credits: [
        {
          name: "Georgios Grigoriadis",
          role: "Programming",
        },
      ],
    },
    {
      id: "mana-and-might",
      image: "/assets/images/manaandmightlogo.png",
      projectName: "Mana and Might",
      projectDesc: "Fast-paced multiplayer PvP arena game for up to 8 players. Pick a class, join the fight, and try to stay alive in a one-shot kill brawl.",
      longDescription: "Mana and Might is a fast-paced multiplayer PvP arena game for up to 8 players. Pick a class, join the fight, and try to stay alive in a one-shot kill brawl. There are no rounds, no timers — just pure chaos.",
      interactable: false,
      footerLink: [
        {
          name: "Itch.io",
          url: "https://ntiregoylfios.itch.io/mana-might",
        },
      ],
      techTags: ["GDevelop", "Prototype"],
      year: 2025,
      platform: "WebGL",
      role: "Gameplay",
      hoverGifEnabled: false,
      detailsPageEnabled: false,
      engine: "GDevelop",
      screenshots: ["https://img.itch.zone/aW1nLzIxNTkwNzQ2LnBuZw==/original/EsmFMd.png"],
      controls: ["WASD — Move", "Left Click — Basic Attack", "Right Click — Class Ability"],
      credits: [
        {
          name: "Georgios Grigoriadis",
          role: "Programming",
        },
        {
          name: "Pavlos Gounaris",
          role: "Artist",
        },
      ],
    },
  ],
  display: true,
};

const jamProjects = {
  title: "GameJams 🏆",
  subtitle: "",
  projects: [
    {
      id: "takis-the-masky-mouse",
      image: "/assets/images/takisthemaskymouselogo.png",
      projectName: "Takis: The Masky Mouse",
      projectDesc: "Fast-paced action game where you swap masks, block and deflect attacks, build charge, and bash a giant mantis-horse boss.",
      longDescription: "Takis: The Masky Mouse is a fast, punchy single-player action game where a tiny mouse survives huge threats using mystical masks, perfect deflects, and brutal shield bashes.",
      interactable: false,
      footerLink: [
        {
          name: "Itch.io",
          url: "https://ntiregoylfios.itch.io/takis-the-masky-mouse",
        },
      ],
      techTags: ["Unity", "Global Game Jam 2026"],
      year: 2026,
      platform: "PC",
      role: "Gameplay & Level Design",
      hoverGifEnabled: false,
      detailsPageEnabled: false,
      engine: "Unity",
      trailerYouTube: "11Kln0NkwFY",
      screenshots: ["https://img.itch.zone/aW1nLzI1MzIzMjQ0LnBuZw==/original/UpmoHH.png"],
      features: [
        "Mask-swapping combat with two equippable slots",
        "Shield with a block/deflect mechanic",
        "Boss fight (in progress)",
      ],
      controls: [
        "WASD — Move",
        "Mouse — Aim (direction / shield facing)",
        "Right Click — Shield Bash (melee attack)",
        "Right Click (hold) — Shield / Block",
        "Right Click (release) — Stop Blocking",
        "Perfect timing while blocking — Deflect / Parry",
        "Space — Toggle Charge Armed (when charge is full)",
        "1 / 2 — Switch Mask Slot",
      ],
      howToPlay: [
        "Equip masks — pick up masks in the world and equip them.",
        "Block to survive — hold Right Click to spawn your shield.",
        "Deflect to build charge — time your block well for a deflect.",
        "Arm the charge — when full, press Space to arm it.",
        "Spend it on a big moment — a Charged Bash or Enhanced Parry.",
      ],
      sections: {
        howToPlay: true,
      },
      credits: [
        {
          name: "Georgios Grigoriadis",
          role: "Programming & Level Design",
        },
        {
          name: "Christos Triantafyllou",
          role: "Programming",
        },
        {
          name: "Myriam Capon",
          role: "UI/UX Coder",
        },
        {
          name: "Pavlos Alifragkis",
          role: "Game Design",
        },
        {
          name: "Adriana Alifragki",
          role: "Art Director",
        },
        {
          name: "Pavlos Gounaris",
          role: "3D Modeling",
        },
        {
          name: "Konstantinos Vasileiadis",
          role: "Spiritual Being",
        },
      ],
    },
    {
      id: "paint-over",
      image: "/assets/images/paintover_logo.png",
      projectName: "Paint Over",
      projectDesc: "Paint Over is a chaotic single-player bullet mayhem game where paint is your power and survival is your goal.",
      longDescription: "Paint Over is a chaotic single-player bullet-mayhem game where paint is your power and survival is your goal. Dominate the buckets with powerful upgrades — just keep moving!",
      interactable: false,
      footerLink: [
        {
          name: "Itch.io",
          url: "https://ntiregoylfios.itch.io/paint-over",
        },
      ],
      techTags: ["Unity", "Mini Jame Gam #43"],
      year: 2025,
      platform: "PC / WebGL",
      role: "Gameplay",
      hoverGifEnabled: false,
      detailsPageEnabled: false,
      engine: "Unity",
      screenshots: [
        "https://img.itch.zone/aW1nLzIxNDg3MjUxLnBuZw==/original/V4ruw9.png",
        "https://img.itch.zone/aW1hZ2UvMzYwOTYyMy8yMTQ4ODYwMS5wbmc=/original/Rd81P1.png",
        "https://img.itch.zone/aW1hZ2UvMzYwOTYyMy8yMTQ4ODYwMi5wbmc=/original/w9z16M.png",
        "https://img.itch.zone/aW1hZ2UvMzYwOTYyMy8yMTQ4ODYwMy5wbmc=/original/YTwV9K.png",
      ],
      controls: ["Mouse / WASD — Move"],
      howToPlay: [
        "Enemies swarm in — you auto-fire.",
        "Level up fast, pick upgrades, unlock new bucket abilities.",
        "Splash your way to victory.",
        "Defeat BUCKY to win.",
      ],
      sections: {
        howToPlay: true,
      },
      credits: [
        {
          name: "Georgios Grigoriadis",
          role: "Programming",
        },
        {
          name: "Christos Triantafyllou",
          role: "Programming",
        },
        {
          name: "Danai Alissa",
          role: "3D Modeler / Artist",
        },
      ],
    },
    {
      id: "crackario",
      image: "/assets/images/crackariologo.png",
      projectName: "Crackario",
      projectDesc: "Chaotic 2D platformer full of meme madness and off-brand heroics.",
      longDescription: "Made in 48 hours for Global Game Jam 2024, Crackario is a chaotic 2D platformer inspired by the classic Mario formula with a wild twist. Join our off-brand plumber hero as he leaps through absurd levels, dodges gun-wielding plants, and blasts his way through memes and madness.",
      interactable: false,
      footerLink: [
        {
          name: "Itch.io",
          url: "https://ntiregoylfios.itch.io/crackario",
        },
      ],
      techTags: ["Unity", "Global Game Jam 2024"],
      year: 2025,
      platform: "PC",
      role: "Gameplay",
      hoverGifEnabled: false,
      detailsPageEnabled: false,
      engine: "Unity",
      duration: "48 hours (Global Game Jam 2024)",
      trailerYouTube: "YVlYDJYHcFQ",
      screenshots: [
        "https://img.itch.zone/aW1nLzIwNjk1NzU3LnBuZw==/original/B1AUuC.png",
        "https://img.itch.zone/aW1hZ2UvMjUwMDU5NC8xNDg1NDY4Ny5wbmc=/original/TF7Kzl.png",
        "https://img.itch.zone/aW1hZ2UvMjUwMDU5NC8xNDg1NDY5MC5wbmc=/original/5g5RMD.png",
        "https://img.itch.zone/aW1hZ2UvMjUwMDU5NC8xNDg1NDY5Mi5wbmc=/original/YI0EOo.png",
      ],
      controls: [
        "Arrow Keys / A & D — Move Left & Right",
        "Spacebar — Jump",
        "Left Click — Fire",
        "ESC — Pause",
      ],
      howToPlay: [
        "Run and jump through levels inspired by old-school platformers.",
        "Avoid traps, enemy bullets, and falling into the void.",
        "Blast hostile flora with your (totally legal) gun.",
        "Reach the flag and survive the madness.",
      ],
      sections: {
        howToPlay: true,
      },
      credits: [
        {
          name: "Georgios Grigoriadis",
          role: "Programming",
        },
        {
          name: "Georgios Tsolakidis",
          role: "Programming",
        },
        {
          name: "Christos Triantafyllou",
          role: "Programming",
        },
        {
          name: "Georgios Tzierakis",
          role: "Art",
        },
      ],
    },
    {
      id: "theescapechapter",
      image: "/assets/images/theescapechapterlogo.png",
      projectName: "TheEscapeChapter",
      projectDesc: "Chill story-driven puzzle game with light magic, mysterious vibes, and... a very strange chest.",
      longDescription: "The Escape Chapter is a chill, story-driven puzzle game where four minds came together to explore teamwork, planning, and the basics of game development. It's filled with light magic, mysterious vibes, and... a very strange chest.",
      interactable: false,
      footerLink: [
        {
          name: "Itch.io",
          url: "https://ntiregoylfios.itch.io/dreamscape",
        },
      ],
      techTags: ["Unity", "GameJamPlus 24/25"],
      year: 2025,
      platform: "PC",
      role: "Gameplay",
      hoverGifEnabled: false,
      detailsPageEnabled: false,
      engine: "Unity",
      screenshots: ["https://img.itch.zone/aW1nLzE4MjU0MzM5LnBuZw==/original/ieZjvE.png"],
      controls: ["WASD / Arrow Keys — Move", "E — Interact", "ESC — Pause"],
      howToPlay: [
        "Wander through a mystical environment filled with gentle puzzles.",
        "Communicate, collaborate, and plan.",
        "Interact with magical objects and uncover hidden clues.",
        "Figure out what that chest is all about... or at least try.",
      ],
      sections: {
        howToPlay: true,
      },
      credits: [
        {
          name: "Georgios Grigoriadis",
          role: "Programming",
        },
        {
          name: "Myriam Kapon",
          role: "Programming",
        },
        {
          name: "Evi Argyriadou",
          role: "Programming",
        },
        {
          name: "Pavlos Alifragkis",
          role: "The Chest",
        },
      ],
    },
    {
      id: "plants-vs-wizards",
      image: "/assets/images/plantsvswizardslogo.png",
      projectName: "Plants Vs Wizards",
      projectDesc: "Local co-op tower defense game where two wizards protect golden trees.",
      longDescription: "Made in 48 hours for Global Game Jam 2023, Plants vs Wizards is a chaotic local co-op tower defense shooter where two wizards must protect the roots of the last golden trees from magical corruption. Team up, cast spells, and survive the onslaught of twisted creatures invading your enchanted forest.",
      interactable: false,
      footerLink: [
        {
          name: "Itch.io",
          url: "https://ntiregoylfios.itch.io/plants-vs-wizards",
        },
      ],
      techTags: ["Unity", "Global Game Jam 2023"],
      year: 2023,
      platform: "PC",
      role: "Gameplay",
      hoverGifEnabled: false,
      detailsPageEnabled: false,
      engine: "Unity",
      duration: "48 hours (Global Game Jam 2023)",
      screenshots: [
        "https://img.itch.zone/aW1nLzIwNzI5MDkxLnBuZw==/original/20vrZ6.png",
        "https://img.itch.zone/aW1hZ2UvMzQ3NDMzNS8yMDcyODk2NS5wbmc=/original/FYAE5J.png",
        "https://img.itch.zone/aW1hZ2UvMzQ3NDMzNS8yMDcyODk2Ni5wbmc=/original/npmU1H.png",
      ],
      controls: [
        "Player 1 — WASD Move, C Shoot, Q Tornado, Left Shift Dash",
        "Player 2 — Arrow Keys Move, J Shoot, K Tornado, Right Shift Dash",
      ],
      howToPlay: [
        "Shoot enemies, use your abilities wisely, and stay alive as long as possible.",
      ],
      sections: {
        howToPlay: true,
      },
      credits: [
        {
          name: "Georgios Grigoriadis",
          role: "Programming",
        },
        {
          name: "Georgios Tsolakidis",
          role: "Programming",
        },
      ],
    },
  ],
  display: true,
};

const websiteProjects = {
  title: "Websites",
  subtitle: "",
  projects: [
    {
      id: "my-portfolio",
      image: "/assets/images/wolflogo.png",
      projectName: "This Site! ^_^",
      projectDesc: "Where my projects live and sometimes break at 3 AM.",
      interactable: false,
      footerLink: [
        {
          name: "Visit",
          url: "https://ggrigoriadis.net/",
        },
      ],
      techTags: ["React JS", "Released"],
      year: 2025,
      platform: "Web",
      role: "Design & Development",
      hoverGifEnabled: false,
      detailsPageEnabled: false,
    },
    {
      id: "amame",
      image: "/assets/images/amamelogo.png",
      projectName: "Amame",
      projectDesc: "Developed for Amame with custom design and responsive layout.",
      interactable: false,
      footerLink: [
        {
          name: "Visit",
          url: "https://www.amame.gr/",
        },
      ],
      techTags: ["WordPress", "Elementor", "Released"],
      imageScale: 0.9,
      year: 2025,
      platform: "Web",
      role: "Design & Development",
      hoverGifEnabled: false,
      detailsPageEnabled: false,
    },
  ],
  display: true,
};

const toolProjects = {
  title: "Tools",
  subtitle: "",
  projects: [
    {
      id: "path-analyzer",
      image: "/assets/images/wolflogo.png",
      projectName: "Path Analyzer",
      projectDesc: "Unity editor tool for recording and visualizing player paths (runs, markers, exports) to help with level design, balancing, and playtesting.",
      interactable: true,
      footerLink: [],
      techTags: ["Unity", "Editor Tool", "Analytics", "In-Development"],
      year: 2026,
      platform: "Unity Editor",
      role: "Programmer",
      hoverGifEnabled: false,
      detailsPageEnabled: false,
    },
  ],
  display: true,
};

const artProjects = {
  title: "Art",
  subtitle: "",
  projects: [],
  display: true,
};

/* ============================================================
   PORTFOLIO SHOWCASE (filterable view)
   ============================================================ */

const SHOWCASE_CATEGORY_ENABLED = {
  games: true,
  gamejams: true,
  tools: true,
  websites: true,
  art: true,
};

// Each category contributes its projects, tagged with the category key the
// showcase filters on. Websites open externally rather than routing to a
// detail page, so their `interactable` is normalised to "link".
const collect = (key, source, transform) =>
  SHOWCASE_CATEGORY_ENABLED[key] && source.display
    ? (source.projects || []).map((p) => ({
        ...p,
        category: key,
        ...(transform ? transform(p) : null),
      }))
    : [];

const portfolioShowcase = {
  title: "Portfolio",
  subtitle: "",
  display: true,

  defaultCategory: "all",

  categories: [
    {
      key: "all",
      label: "All",
    },
    {
      key: "games",
      label: "Games",
    },
    {
      key: "gamejams",
      label: "Game Jams",
    },
    {
      key: "tools",
      label: "Tools",
    },
    {
      key: "websites",
      label: "Websites",
    },
    {
      key: "art",
      label: "Art",
    },
  ],

  featuredIds: ["hover-lasers", "rum-ram", "paint-over", "my-portfolio"],

  items: [
    ...collect("games", bigProjects),
    ...collect("gamejams", jamProjects),
    ...collect("tools", toolProjects),
    ...collect("websites", websiteProjects, (p) => ({
      interactable: p.interactable === false ? "link" : p.interactable,
    })),
    ...collect("art", artProjects),
  ],
};

/* ============================================================
   (OPTIONAL) Achievement Section
   ============================================================ */

const achievementSection = {
  title: emoji("GameJams 🏆 "),
  subtitle: "",
  achievementsCards: [
    {
      title: "Plants Vs Wizards",
      subtitle: "Local co-op tower defense game where two wizards protect golden trees.",
      image: "/assets/images/plantsvswizardslogo.png",
      footerLink: [
        {
          name: "Itch.io",
          url: "https://ntiregoylfios.itch.io/plants-vs-wizards",
        },
      ],
      techTags: ["Unity", "Global Game Jam 2023"],
    },
  ],
  display: false,
};

/* ============================================================
   (OPTIONAL) Blogs / Talks / Podcast
   ============================================================ */

const blogSection = {
  title: "Blogs",
  subtitle: "With love for developing cool stuff, I enjoy teaching others what I've learned.",
  displayMediumBlogs: "true",
  blogs: [
    {
      url: "https://blog.usejournal.com/create-a-google-assistant-action-and-win-a-google-t-shirt-and-cloud-credits-4a8d86d76eae",
      title: "Win a Google Assistant Tshirt and $200 in Google Cloud Credits",
      description: "Create a Google Assistant Action in less than 30 minutes and win rewards.",
    },
    {
      url: "https://medium.com/@saadpasta/why-react-is-the-best-5a97563f423e",
      title: "Why REACT is The Best?",
      description: "React is a JavaScript library for building user interfaces.",
    },
  ],
  display: false,
};

const talkSection = {
  title: "TALKS",
  subtitle: emoji("I LOVE TO SHARE MY LIMITED KNOWLEDGE AND GET A SPEAKER BADGE 😅"),
  talks: [
    {
      title: "Build Actions For Google Assistant",
      subtitle: "Codelab at GDG DevFest Karachi 2019",
      slides_url: "https://bit.ly/saadpasta-slides",
      event_url: "https://www.facebook.com/events/2339906106275053/",
    },
  ],
  display: false,
};

const podcastSection = {
  title: emoji("Podcast 🎙️"),
  subtitle: "I LOVE TO TALK ABOUT MYSELF AND TECHNOLOGY",
  podcast: [
    "https://anchor.fm/codevcast/embed/episodes/DevStory---Saad-Pasta-from-Karachi--Pakistan-e9givv/a-a15itvo",
  ],
  display: false,
};

/* ============================================================
   RESUME / CONTACT / TWITTER
   ============================================================ */

const resumeSection = {
  resumeFile: "/assets/images/ggrigoriadis_cv.pdf",
};

const contactInfo = {
  title: emoji("Contact Me"),
  subtitle: "Discuss a project or just want to say hi? My inbox is open to all.",
  number: "",
  email_address: "giorgosgrigoriadis9@gmail.com",
  profile_image: "/assets/images/pfp.png",
  methods: {},
  openFor: null,
};

const twitterDetails = {
  userName: "ggrigoriadis",
  display: false,
};

/* ============================================================
   HIREABLE FLAG
   ============================================================ */

const isHireable = true;

/* ============================================================
   EXPORTS (do not rename existing exports)
   ============================================================ */

export {
  theme,
  branding,
  seo,
  footer,
  tagCatalog,
  tagColorByName,
  illustration,
  greeting,
  socialMediaLinks,
  splashScreen,
  skillsSection,
  educationInfo,
  techStack,
  workExperiences,
  openSource,
  bigProjects,
  jamProjects,
  websiteProjects,
  toolProjects,
  artProjects,
  portfolioShowcase,
  achievementSection,
  blogSection,
  talkSection,
  podcastSection,
  contactInfo,
  twitterDetails,
  isHireable,
  resumeSection,
};
